import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import LoadCard from '../components/LoadCard'
import LoadTable from '../components/LoadTable'
import SubmitButton from '../components/SubmitButton'
import TagHeader from '../components/TagHeader'
import type { Stop } from '../constants/stopsData'
import { useTagContext } from '../contexts/TagContext'
import { type LoadResponse, loadService } from '../services/loadService'

const LoadDashboard: React.FC = () => {
  const { tagId } = useParams()
  const navigate = useNavigate()
  const [loads, setLoads] = useState<LoadResponse[]>([])
  const { getCurrentTag } = useTagContext()
  const [isLoading, setIsLoading] = useState(true)

  const currentTag = getCurrentTag(tagId)
  const currentLoadNumber = loads.length

  useEffect(() => {
    const fetchLoads = async () => {
      if (tagId) {
        try {
          const loadData = await loadService.getLoadsByTag(tagId)
          // TODO: do this on the backend, return loads in reverse order
          loadData.reverse()
          setLoads(loadData)
        } catch (error) {
          console.error('Failed to load data:', error)
        }
      }
      setIsLoading(false)
    }

    fetchLoads()
  }, [tagId])

  const handleAddNewLoad = () => {
    if (tagId) {
      const newLoadNumber = loads.length + 1
      navigate(`/tags/open/${tagId}/${newLoadNumber}/details`)
    }
  }

  const handleEditLoad = (loadNumber: number, loadId: number) => {
    if (tagId) {
      navigate(`/tags/open/${tagId}/${loadNumber}/details`, {
        state: { loadId },
      })
    }
  }

  return (
    <div className={css(loadDashboardStyles.pageContainer)}>
      <TagHeader
        tagId={tagId}
        title={`Summary Load #${currentLoadNumber}`}
        backUrl="/tags/open"
        status={currentTag?.status || 'IN_PROGRESS'}
      />

      <div className={css(loadDashboardStyles.container)}>
        <div className={css(loadDashboardStyles.tableSection)}>
          {tagId && <LoadTable tagId={tagId} />}
        </div>
        <div className={css(loadDashboardStyles.submitButtonContainer)}>
          <button
            className={css(loadDashboardStyles.addButton)}
            onClick={handleAddNewLoad}
          >
            <i className="fa-solid fa-plus-circle" />
            <span>{`Add New Load #${currentLoadNumber + 1}`}</span>
          </button>
          <SubmitButton
            canSubmit={true}
            text="Complete Tag"
            onClick={() => navigate(`/tags/open/${tagId}/loads/confirmation`)}
          />
        </div>
      </div>
    </div>
  )
}

export const loadDashboardStyles = StyleSheet.create({
  pageContainer: {
    alignItems: 'center',
    margin: '0 auto',
    backgroundColor: '#F9FAFB',
  },
  container: {
    alignItems: 'center',
    margin: '0 auto',
    marginTop: '36px',
    backgroundColor: '#F9FAFB',
    paddingBottom: 150,
    width: '100%',
  },
  headerSection: {
    height: '157px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  navbar: {
    width: '100%',
    height: '64px',
    background: '#1F2A37',
  },
  navbarContent: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '0 16px',
    height: '63px',
    justifyContent: 'space-between',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    width: '80px',
    height: '32px',
  },
  menuButton: {
    width: '32px',
    height: '32px',
    background: 'none',
    border: 'none',
    color: '#9CA3AF',
    cursor: 'pointer',
  },
  rightContent: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingLeft: '16px',
    gap: '12px',
    width: '278px',
    height: '32px',
  },
  avatar: {
    width: '32px',
    height: '32px',
    borderRadius: '16px',
    background: '#F3F4F6',
  },
  divider: {
    width: '100%',
    height: '1px',
    background: '#4B5563',
  },
  heading: {
    padding: '16px',
    background: '#FFFFFF',
    width: '100%',
  },
  headingContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  title: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#111928',
    lineHeight: '150%',
    fontFamily: 'Inter',
  },
  tagInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  tagId: {
    color: '#046C4E',
    fontWeight: 600,
    fontSize: '16px',
  },
  badge: {
    background: '#DEF7EC',
    padding: '2px 12px',
    borderRadius: '6px',
    color: '#03543F',
    fontSize: '14px',
    fontWeight: 600,
  },
  formSection: {
    padding: '20px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  loadDetails: {
    padding: '24px',
    background: '#FFFFFF',
    border: '1px solid #F3F4F6',
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.08)',
    borderRadius: '8px',
    minHeight: '100vh',
    '@media only screen and (max-width: 767px)': {
      minHeight: 'calc(100vh - 70px)',
    },
  },
  loadHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  editButton: {
    width: '36px',
    height: '36px',
    background: '#1C64F2',
    borderRadius: '999px',
    border: 'none',
    color: '#FFFFFF',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  detailsGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  detailRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
  },
  label: {
    width: '120px',
    fontSize: '12px',
    fontWeight: 600,
    color: '#4B5563',
    textTransform: 'uppercase',
    fontFamily: 'Inter',
  },
  value: {
    fontSize: '16px',
    fontWeight: 500,
    color: '#111928',
    fontFamily: 'Inter',
  },
  dividerLine: {
    height: '1px',
    background: '#E5E7EB',
    margin: '15px 0',
  },
  actionSection: {
    padding: '20px 16px',
    borderTop: '1px solid #E5E7EB',
  },
  confirmButton: {
    width: '100%',
    height: '48px',
    background: '#1C64F2',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 500,
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  addButtonContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 16,
    width: '100%',
  },
  addButton: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '12px 20px',
    gap: '12px',
    height: '48px',
    border: '1px solid #1C64F2',
    borderRadius: '8px',
    background: '#FFFFFF',
    color: '#1C64F2',
    fontFamily: 'Inter',
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '150%',
    cursor: 'pointer',
    width: '90%',
  },
  spacer: {
    height: '80px',
  },
  tableSection: {
    width: '100%',
  },
  submitButtonContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    maxWidth: '800px',
    margin: '0 auto',
    gap: '16px',
    marginTop: '20px',
  },
})

export default LoadDashboard
